// components/common/Footer.js
import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { motion } from 'framer-motion';

const COL_LINKS = [
  {
    title: 'Product',
    links: [
      { label: 'Demo', path: '/demo' },
      { label: 'Pricing', path: '/pricing' },
      { label: 'AI Video Dubbing', path: '/ai-video-dubbing' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About', path: '/about-us' },
      { label: 'Contact', path: '/contact' },
    ],
  },
  {
    title: 'Use Cases',
    links: [
      { label: 'Creators', path: '/use-cases/creators' },
      { label: 'Media Companies', path: '/use-cases/media' },
      { label: 'EdTech', path: '/use-cases/edtech' },
    ],
  },
  {
    title: 'Compare',
    links: [
      { label: 'vs Deepdub', path: '/vs/deepdub' },
      { label: 'vs HeyGen', path: '/vs/heygen' },
      { label: 'vs Traditional', path: '/vs/traditional-dubbing' },
    ],
  },
  {
    title: 'Languages',
    links: [
      { label: 'All Languages', path: '/languages' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', path: '/privacy' },
      { label: 'Terms of Service', path: '/terms' },
    ],
  },
];

const SOCIALS = [
  { Icon: LinkedInIcon, url: 'https://www.linkedin.com/company/aiguruhq' },
  { Icon: InstagramIcon, url: 'https://www.instagram.com/aiguruhq/' },
  { Icon: TwitterIcon, url: 'https://x.com/aiguruin' },
  { Icon: YouTubeIcon, url: 'https://www.youtube.com/@aiguruhq' },
];

const Footer = () => {
  const navigate = useNavigate();

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#000000',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        pt: { xs: 6, md: 10 },
        pb: 4,
        px: { xs: 3, md: 8 },
      }}
    >
      <Box
        sx={{
          maxWidth: '1200px',
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '280px 1fr' },
          gap: { xs: 6, md: 10 },
          mb: 8,
        }}
      >
        {/* Brand column */}
        <Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '22px',
              letterSpacing: '-0.04em',
              fontFamily: 'Inter, sans-serif',
              mb: 2,
              cursor: 'pointer',
            }}
            onClick={() => navigate('/')}
          >
            <Box component="span" sx={{ color: '#FFFFFF' }}>Dub</Box>
            <Box component="span" sx={{ color: 'rgba(255,255,255,0.35)' }}>Labs</Box>
          </Typography>

          <Typography
            sx={{
              color: 'rgba(255,255,255,0.4)',
              fontSize: '14px',
              lineHeight: 1.7,
              fontFamily: 'Inter, sans-serif',
              mb: 3,
              maxWidth: '240px',
            }}
          >
            Professional AI dubbing with perfect lip-sync. Built for media companies.
          </Typography>

          {/* Socials */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {SOCIALS.map(({ Icon, url }, i) => (
              <IconButton
                key={i}
                component={motion.button}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.open(url, '_blank')}
                sx={{
                  width: 34,
                  height: 34,
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: 'rgba(255,255,255,0.45)',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    color: '#FFFFFF',
                    borderColor: 'rgba(255,255,255,0.2)',
                  },
                }}
              >
                <Icon sx={{ fontSize: 16 }} />
              </IconButton>
            ))}
          </Box>
        </Box>

        {/* Link columns */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
            gap: { xs: 4, md: 3 },
          }}
        >
          {COL_LINKS.map((col) => (
            <Box key={col.title}>
              <Typography
                sx={{
                  color: '#FFFFFF',
                  fontWeight: 600,
                  fontSize: '13px',
                  fontFamily: 'Inter, sans-serif',
                  letterSpacing: '0.02em',
                  mb: 2,
                }}
              >
                {col.title}
              </Typography>
              {col.links.map((link) => (
                <Typography
                  key={link.label}
                  onClick={() => navigate(link.path)}
                  sx={{
                    color: 'rgba(255,255,255,0.38)',
                    fontSize: '13px',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    mb: 1.25,
                    cursor: 'pointer',
                    transition: 'color 0.2s ease',
                    '&:hover': { color: 'rgba(255,255,255,0.75)' },
                  }}
                >
                  {link.label}
                </Typography>
              ))}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Bottom bar */}
      <Box
        sx={{
          maxWidth: '1200px',
          mx: 'auto',
          pt: 3,
          borderTop: '1px solid rgba(255,255,255,0.05)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography sx={{ color: 'rgba(255,255,255,0.22)', fontSize: '12px', fontFamily: 'Inter, sans-serif' }}>
          © 2025 DubLabs. All rights reserved. DubLabs™ is a trademark of AI Guru, Inc.
        </Typography>
        <Typography
          onClick={() => window.open('https://aiguruhq.com', '_blank')}
          sx={{
            color: 'rgba(255,255,255,0.22)',
            fontSize: '12px',
            fontFamily: 'Inter, sans-serif',
            cursor: 'pointer',
            '&:hover': { color: 'rgba(255,255,255,0.5)' },
          }}
        >
          Built by AI Guru
        </Typography>
      </Box>
    </Box>
  );
};

export default Footer;
